import { useState } from 'react';

interface HeaderProps {
  isScrolled: boolean;
}

export default function Header({ isScrolled }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/8e1dd29798fcb43b2566dc879e04c9bd/e225e6f5cfb761e68abf456757fc7535.jpeg"
                alt="Logo Minha Casa Minha Vida"
                className="h-12 w-auto"
              />
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('calculadora')}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Calculadora
              </button>
              <button
                onClick={() => scrollToSection('beneficios')}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Benefícios
              </button>
              <button
                onClick={() => scrollToSection('garantia')}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                Garantia
              </button>
              <button
                onClick={() => scrollToSection('formulario')}
                className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar Contato
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('calculadora')}
                className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium cursor-pointer whitespace-nowrap"
              >
                Calculadora
              </button>
              <button
                onClick={() => scrollToSection('beneficios')}
                className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium cursor-pointer whitespace-nowrap"
              >
                Benefícios
              </button>
              <button
                onClick={() => scrollToSection('garantia')}
                className="block w-full text-left text-gray-700 hover:text-teal-600 font-medium cursor-pointer whitespace-nowrap"
              >
                Garantia
              </button>
              <button
                onClick={() => scrollToSection('formulario')}
                className="block w-full bg-teal-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar Contato
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://public.readdy.ai/ai/img_res/a04999a6dfed5bb0430ebed39402090e.jpg"
            className="w-full h-full object-cover"
          >
            <source
              src="https://videos.pexels.com/video-files/5473765/5473765-uhd_2560_1440_25fps.mp4"
              type="video/mp4"
            />
            <source
              src="https://videos.pexels.com/video-files/6894223/6894223-uhd_2560_1440_25fps.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        {/* Fallback Background Image */}
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: "url('https://readdy.ai/api/search-image?query=Aerial%20view%20of%20affordable%20housing%20complex%20in%20Brazil%2C%20Minha%20Casa%20Minha%20Vida%20program%20residential%20neighborhood%20with%20simple%20colorful%20houses%20in%20organized%20rows%2C%20working%20class%20families%20moving%20into%20new%20homes%20with%20keys%2C%20social%20housing%20development%20with%20community%20spaces%20and%20green%20areas%2C%20hopeful%20atmosphere%20showing%20dreams%20coming%20true%20for%20low%20income%20families%2C%20documentary%20style%20photography%2C%20warm%20natural%20lighting%2C%20realistic%20Brazilian%20urban%20landscape&width=1920&height=1080&seq=mcmv-hero-bg-v1&orientation=landscape')",
            zIndex: -1
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Realize o Sonho da Casa Própria com o Minha Casa Minha Vida
          </h1>
          <p className="text-lg sm:text-xl text-white/95 mb-8 max-w-2xl mx-auto">
            Financiamento aprovado garantido para quem tem nome limpo e renda comprovada. Simule agora e descubra como é fácil conquistar seu lar.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollToSection('calculadora')}
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Simular Financiamento
            </button>
            <button
              onClick={() => scrollToSection('formulario')}
              className="bg-white text-teal-600 px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-50 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Falar com Especialista
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-white"></i>
        </div>
      </section>
    </>
  );
}
